import { useNavigate } from "react-router-dom";
import { theme } from "../theme";

const section: React.CSSProperties = {
  marginBottom: 32,
};

const h1Style: React.CSSProperties = {
  fontSize: 26,
  fontWeight: 800,
  color: "var(--c-heading)",
  marginBottom: 4,
  lineHeight: 1.2,
};

const h2Style: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: "var(--c-heading)",
  marginBottom: 8,
  marginTop: 0,
};

const h3Style: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: "var(--c-heading)",
  marginBottom: 6,
  marginTop: 0,
};

const pStyle: React.CSSProperties = {
  fontSize: 13,
  color: "var(--c-text)",
  lineHeight: 1.7,
  margin: "0 0 10px 0",
};

const liStyle: React.CSSProperties = {
  fontSize: 13,
  color: "var(--c-text)",
  lineHeight: 1.7,
  marginBottom: 4,
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 12,
  marginTop: 12,
  marginBottom: 8,
};

const thStyle: React.CSSProperties = {
  background: "var(--c-panelAlt)",
  color: "var(--c-heading)",
  fontWeight: 700,
  padding: "8px 10px",
  textAlign: "left",
  border: "1px solid var(--c-border)",
  borderBottom: "1px solid var(--c-line)",
  fontSize: 12,
};

const tdStyle: React.CSSProperties = {
  padding: "8px 10px",
  color: "var(--c-text)",
  border: "1px solid var(--c-border)",
  verticalAlign: "top",
  lineHeight: 1.5,
};

const dividerStyle: React.CSSProperties = {
  border: "none",
  borderTop: "1px solid var(--c-border)",
  margin: "0 0 32px 0",
};

export function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--c-bg)",
        fontFamily: theme.font,
        padding: "48px 16px 80px",
      }}
    >
      <div style={{ maxWidth: 760, margin: "0 auto" }}>

        {/* Back link */}
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "none",
            border: "none",
            color: "var(--c-accent)",
            fontSize: 13,
            cursor: "pointer",
            padding: 0,
            marginBottom: 32,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          ← Back
        </button>

        {/* Header */}
        <div style={section}>
          <h1 style={h1Style}>Abacas Security Compliance Platform — Privacy Policy</h1>
          <p style={{ ...pStyle, color: "var(--c-muted)", marginBottom: 0 }}>
            Effective date: 29 July 2026
          </p>
        </div>

        <hr style={dividerStyle} />

        {/* 1. Introduction */}
        <div style={section}>
          <h2 style={h2Style}>1. Introduction</h2>
          <p style={pStyle}>
            This Privacy Policy explains how Abacas ("we", "us", "our") collects, uses, stores and
            protects personal data when you use the Abacas Security Compliance Platform, our multi-tenant
            service for managing compliance with ISO 27001, ISO 27002, ISO 9001, ISO 42001, Cyber Essentials, Cyber
            Essentials Plus, SOC 2, PCI DSS and related standards.
          </p>
          <p style={pStyle}>
            This policy applies to all users of the platform across our three account roles: Clients,
            Consultants, and Auditors, each of which may see different sections of this policy applied
            to them as noted below.
          </p>
          <p style={pStyle}>
            We process personal data in line with general data protection principles (lawfulness,
            fairness, transparency, purpose limitation, data minimisation, accuracy, storage
            limitation, integrity and confidentiality, and accountability), consistent with
            GDPR-equivalent standards.
          </p>
        </div>

        {/* 2. Data We Collect */}
        <div style={section}>
          <h2 style={h2Style}>2. Data We Collect, By Role</h2>
          <p style={pStyle}>
            The table below summarises the personal and organisational data collected through the
            platform's core architecture (Microsoft Entra ID multi-tenant OIDC authentication for
            Clients and Consultants; local JWT authentication for Auditor/Bureau accounts; PostgreSQL
            data store).
          </p>

          <h3 style={h3Style}>2.1 All roles — account &amp; identity data</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Examples of data</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}>Identity data</td>
                  <td style={tdStyle}>Name, work email, organisation name, tenant ID</td>
                  <td style={tdStyle}>Entra ID OIDC (Clients/Consultants) or registration form (Auditors)</td>
                  <td style={tdStyle}>Account creation, authentication, tenant isolation</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Authentication data</td>
                  <td style={tdStyle}>OIDC tokens; for Auditors, hashed password and local JWT session tokens</td>
                  <td style={tdStyle}>Microsoft Entra ID / local auth service</td>
                  <td style={tdStyle}>Secure login and session management</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Usage &amp; audit logs</td>
                  <td style={tdStyle}>Login timestamps, actions taken, IP address, user agent</td>
                  <td style={tdStyle}>Application logging middleware</td>
                  <td style={tdStyle}>Security monitoring, audit trail, ISO 27001-aligned access logging</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 style={{ ...h3Style, marginTop: 20 }}>2.2 Client accounts</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Examples of data</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}>Compliance evidence</td>
                  <td style={tdStyle}>Uploaded documents, screenshots, policy files, control evidence</td>
                  <td style={tdStyle}>Direct upload via evidence module</td>
                  <td style={tdStyle}>Populate Statement of Applicability, support audits and gap analysis</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Organisational data</td>
                  <td style={tdStyle}>Company structure, asset registers, risk register entries, SoA responses</td>
                  <td style={tdStyle}>Manual entry / forms</td>
                  <td style={tdStyle}>Generate gap analyses, compliance reports and dashboards</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Billing data</td>
                  <td style={tdStyle}>Service tier, per-control spend, invoicing contact details</td>
                  <td style={tdStyle}>Billing module</td>
                  <td style={tdStyle}>Tier enforcement, invoicing, spend cap alerts</td>
                </tr>
                <tr>
                  <td style={tdStyle}>AI-processed compliance data</td>
                  <td style={tdStyle}>Control statuses, SoA responses, risk register entries and scope notes submitted to AI compliance checks or risk suggestion jobs</td>
                  <td style={tdStyle}>Compliance Check and Risk Register modules (via Anthropic Claude API)</td>
                  <td style={tdStyle}>AI-assisted gap analysis, control recommendations and risk identification</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Third-party integration data</td>
                  <td style={tdStyle}>Microsoft 365 documents and calendar entries; Intune device and policy data; Microsoft Sentinel security events; Microsoft Defender alerts; Google Workspace documents; firewall configuration metadata</td>
                  <td style={tdStyle}>Optional integrations connected by the organisation's administrator (M365, Intune, Sentinel, Google Workspace, Firewall)</td>
                  <td style={tdStyle}>Automated evidence collection, document tagging, security posture monitoring and audit schedule synchronisation</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Legal and regulatory obligation data</td>
                  <td style={tdStyle}>Selected regulations (e.g. UK GDPR, NIS 2), compliance status and justification notes per obligation</td>
                  <td style={tdStyle}>Legal Compliance module (manual entry)</td>
                  <td style={tdStyle}>Track regulatory obligations alongside technical compliance standards</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 style={{ ...h3Style, marginTop: 20 }}>2.3 Consultant accounts</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Examples of data</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}>Engagement data</td>
                  <td style={tdStyle}>Assigned clients, engagement notes, recommendations issued</td>
                  <td style={tdStyle}>Platform activity</td>
                  <td style={tdStyle}>Service delivery, bureau fee/markup calculation</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Professional data</td>
                  <td style={tdStyle}>Qualifications, specialisms (where supplied)</td>
                  <td style={tdStyle}>Onboarding form</td>
                  <td style={tdStyle}>Client-consultant matching, service quality</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 style={{ ...h3Style, marginTop: 20 }}>2.4 Auditor accounts</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Examples of data</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}>Audit records</td>
                  <td style={tdStyle}>Findings, feedback, review calendar entries, sign-offs</td>
                  <td style={tdStyle}>Audit module</td>
                  <td style={tdStyle}>Independent audit trail, evidence review, certification support</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Access credentials</td>
                  <td style={tdStyle}>Local username, hashed password</td>
                  <td style={tdStyle}>Registration by bureau administrator</td>
                  <td style={tdStyle}>Local JWT authentication (Auditors are not provisioned via Entra ID)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 3. How We Use Personal Data */}
        <div style={section}>
          <h2 style={h2Style}>3. How We Use Personal Data</h2>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            {[
              "To provide, operate and maintain the platform, including tier-gated features enforced server-side",
              "To authenticate users and maintain tenant-level data isolation in our multi-tenant architecture",
              "To generate compliance artefacts: gap analyses, Statements of Applicability, executive reports",
              "To maintain audit logs required for ISO 27001/27002-aligned access control and accountability",
              "To run AI-assisted compliance checks and risk suggestions by submitting relevant organisational data to the Anthropic Claude API (see Section 6)",
              "To synchronise documents, device data, security events and calendar entries from connected third-party platforms (Microsoft 365, Intune, Sentinel, Google Workspace, Firewall) where an integration has been enabled by the organisation's administrator",
              "To track and report compliance status against legal and regulatory obligations (e.g. UK GDPR, NIS 2) selected by the organisation",
              "To bill for services and enforce per-control spend caps",
              "To respond to support requests and investigate security incidents",
              "To improve platform reliability, informed by aggregated, de-identified usage patterns where possible",
            ].map((item, i) => (
              <li key={i} style={liStyle}>{item}</li>
            ))}
          </ul>
        </div>

        {/* 4. Legal Basis */}
        <div style={section}>
          <h2 style={h2Style}>4. Legal Basis for Processing</h2>
          <p style={pStyle}>
            Where a GDPR-equivalent framework applies, we rely on the following bases:
          </p>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            {[
              "Contract — processing necessary to deliver the service you or your organisation has subscribed to",
              "Legitimate interests — platform security, audit logging, fraud prevention, and service improvement",
              "Legal obligation — where retention or disclosure is required by applicable law or regulation",
              "Consent — for any optional communications or non-essential cookies, where applicable",
            ].map((item, i) => (
              <li key={i} style={liStyle}>{item}</li>
            ))}
          </ul>
        </div>

        {/* 5. Data Storage & Security */}
        <div style={section}>
          <h2 style={h2Style}>5. Data Storage &amp; Security</h2>
          <p style={pStyle}>
            The Abacas Security Compliance Platform is hosted on Microsoft Azure. Technical and organisational measures include:
          </p>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            {[
              "Azure Container Apps for the API layer, with network-level access controls",
              "Azure Static Web Apps for the frontend",
              "Azure Key Vault with Managed Identity for secrets and credential management — no plaintext secrets in application code",
              "PostgreSQL database with tenant-isolated data access enforced at the application layer",
              "Encryption in transit (TLS) for all client-server communication",
              "Role-based access control across Client, Consultant and Auditor roles, enforced server-side via middleware",
            ].map((item, i) => (
              <li key={i} style={liStyle}>{item}</li>
            ))}
          </ul>
          <p style={{ ...pStyle, marginTop: 10 }}>
            No system can guarantee absolute security. We continually review our architecture —
            including recurring security and fuzz testing — to identify and remediate risk.
          </p>
        </div>

        {/* 6. Data Sharing */}
        <div style={section}>
          <h2 style={h2Style}>6. Data Sharing &amp; Disclosure</h2>
          <p style={pStyle}>
            We do not sell personal data. Data may be shared in the following limited circumstances:
          </p>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            {[
              "Between roles within the same engagement, where necessary for service delivery (e.g. a Client's evidence is visible to their assigned Consultant and, for audit purposes, to Auditors)",
              "With Microsoft, as our cloud infrastructure and identity provider, acting under its own data processing terms",
              "With Anthropic (via the Claude API), where an organisation uses the AI compliance check or risk suggestion features — only the compliance-relevant data required to generate the analysis is transmitted; Anthropic's API data-handling terms apply and data submitted via the API is not used to train Anthropic's models",
              "With third-party platform providers (Microsoft for M365, Intune and Sentinel integrations; Google for Workspace integrations) when the organisation's administrator enables the relevant connection — data flows are limited to what is necessary for the integration's stated purpose",
              "Where required by law, regulation, or a valid legal process",
              "With professional advisers (legal, audit, insurance) under confidentiality obligations, where necessary",
            ].map((item, i) => (
              <li key={i} style={liStyle}>{item}</li>
            ))}
          </ul>
        </div>

        {/* 7. International Transfers */}
        <div style={section}>
          <h2 style={h2Style}>7. International Transfers</h2>
          <p style={pStyle}>
            Data may be processed in data centre regions selected as part of our Azure deployment.
            Where data is transferred across borders, we take steps to ensure an equivalent level of
            protection is maintained, consistent with recognised transfer mechanisms.
          </p>
        </div>

        {/* 8. Data Retention */}
        <div style={section}>
          <h2 style={h2Style}>8. Data Retention</h2>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            {[
              "Account and identity data: retained for the duration of the account plus a limited period thereafter for legal and audit purposes",
              "Compliance evidence and audit records: retained in line with the retention expectations of the relevant standard (e.g. ISO 27001 surveillance cycles) and contractual agreement",
              "Audit/access logs: retained for a defined period sufficient for security investigation, then deleted or anonymised",
              "Billing records: retained as required for tax and accounting obligations",
            ].map((item, i) => (
              <li key={i} style={liStyle}>{item}</li>
            ))}
          </ul>
        </div>

        {/* 9. Your Rights */}
        <div style={section}>
          <h2 style={h2Style}>9. Your Rights</h2>
          <p style={pStyle}>Subject to applicable law, you may have the right to:</p>
          <ul style={{ paddingLeft: 20, margin: "0 0 10px 0" }}>
            {[
              "Access the personal data we hold about you",
              "Request correction of inaccurate data",
              "Request erasure, subject to our legal and contractual retention obligations",
              "Object to or restrict certain processing",
              "Request a portable copy of your data",
              "Withdraw consent, where processing is based on consent",
            ].map((item, i) => (
              <li key={i} style={liStyle}>{item}</li>
            ))}
          </ul>
          <p style={pStyle}>
            Requests can be made through your organisation's account administrator or by contacting
            us directly using the details in Section 12.
          </p>
        </div>

        {/* 10. Cookies */}
        <div style={section}>
          <h2 style={h2Style}>10. Cookies &amp; Session Data</h2>
          <p style={pStyle}>
            The platform uses strictly necessary cookies/session tokens to maintain authentication
            state (OIDC session for Clients/Consultants; JWT session for Auditors). We do not use
            third-party advertising cookies.
          </p>
        </div>

        {/* 11. Changes */}
        <div style={section}>
          <h2 style={h2Style}>11. Changes to This Policy</h2>
          <p style={pStyle}>
            We may update this policy as the platform evolves, particularly as new compliance
            standards or features are added. Material changes will be notified to account
            administrators.
          </p>
        </div>

        {/* 12. Contact */}
        <div style={section}>
          <h2 style={h2Style}>12. Contact</h2>
          <p style={pStyle}>
            For questions about this policy or to exercise your data protection rights, please
            contact your account administrator or Abacas directly.
          </p>
        </div>

        <hr style={dividerStyle} />
        <p style={{ ...pStyle, color: "var(--c-muted)", fontSize: 12 }}>
          Abacas Security — <a href="https://app.abacassecurity.com" style={{ color: "var(--c-accent)" }}>app.abacassecurity.com</a>
        </p>
      </div>
    </div>
  );
}
